import React, { useState, useEffect } from "react";
import { Input } from "../../Helpers/Input/Input";
import trans from "../../../utils";
import { useFormikContext } from "formik";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import FileUploader from "../../Helpers/Dropzone/FileUploader";
import EditAttachments from "../../Helpers/Dropzone/EditAttachments";
import { useGetPOIZonesByParentIdQuery } from "../../../Redux/service/POIS/POIs";
import {
  useGetAllCompaniesQuery,
  useGetCompanyProjectsQuery,
  useGetCompanyProjectTradesQuery,
} from "../../../Redux/service/Companies/Company";
import { useGetAllUsersQuery } from "../../../Redux/service/Users/Users";

const roleOptions = [
  { label: trans("Contractor"), value: "CONTRACTOR" },
  { label: trans("Sub-Contractor"), value: "SUB_CONTRACTOR" },
  { label: trans("QC"), value: "QC" },
  { label: trans("Consultant"), value: "CONSULTANT" },
];

const TeamFormBody = ({ isEditing, editData }) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();

  const [attachments, setAttachments] = useState(
    isEditing && editData?.data?.attachments ? editData.data.attachments : [],
  );
  const [newAttachments, setNewAttachments] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  useEffect(() => {
    if (isEditing && editData?.data?.attachments) {
      setAttachments(editData.data.attachments);
    }
    // Set company ID from edit data if available
    if (isEditing && editData?.data?.companyProject?.companyId) {
      setSelectedCompanyId(editData.data.companyProject.companyId);
    }
  }, [isEditing, editData]);

  // Fetch all companies for company dropdown
  const { data: companiesData } = useGetAllCompaniesQuery({
    page: 1,
    limit: 10000,
  });

  // Fetch company projects based on selected company
  const { data: companyProjectsData, isFetching: projectsLoading } =
    useGetCompanyProjectsQuery(selectedCompanyId, {
      skip: !selectedCompanyId,
    });

  // Fetch trades based on selected company project
  const { data: companyProjectTradesData, isFetching: tradesLoading } =
    useGetCompanyProjectTradesQuery(values.companyProjectId, {
      skip: !values.companyProjectId,
    });

  // Fetch all users for member selection
  const { data: usersData } = useGetAllUsersQuery({
    page: 1,
    limit: 10000,
  });

  const companyOptions =
    companiesData?.data?.map((company) => ({
      label: company.name,
      value: company.id,
    })) || [];

  const companyProjectOptions =
    companyProjectsData?.data?.map((cp) => ({
      label: cp.project?.name || `Project ${cp.projectId}`,
      value: cp.id, // This is the companyProjectId
      projectId: cp.projectId,
    })) || [];

  const tradeOptions =
    companyProjectTradesData?.data?.trades?.map((trade) => ({
      label: trans(trade?.name),
      value: trade.tradeId,
    })) || [];

  const zoneOptions =
    companyProjectTradesData?.data?.assignedZones?.map((zone) => ({
      label: trans(zone?.name),
      value: zone?.id,
    })) || [];

  const userOptions =
    usersData?.data?.map((user) => ({
      label: user.name || user.email,
      value: user.id,
    })) || [];

  const handleFileSelect = (files) => {
    setNewAttachments(files);
    setFieldValue("attachments", files);
  };

  return (
    <div className="form-section">
      <div className="row">
        <div className="col-lg-6">
          <Input
            title={trans("Team Code")}
            type="text"
            placeholder={trans("Enter team code")}
            formstyle="form-input"
            id="code"
            name="code"
            value={values.code}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.code}
            touched={touched.code}
          />
        </div>
        <div className="col-lg-6">
          <Input
            title={trans("Team Name")}
            type="text"
            placeholder={trans("Enter team name")}
            formstyle="form-input"
            id="name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.name}
            touched={touched.name}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="companyId"
            name="companyId"
            label={trans("Company")}
            placeholder={trans("Select Company")}
            options={companyOptions}
            value={selectedCompanyId}
            onChange={(val) => {
              setSelectedCompanyId(val);
              // Clear dependent fields when company changes
              setFieldValue("companyProjectId", "");
              setFieldValue("trades", []);
              setFieldValue("assignedZones", []);
            }}
            onBlur={() => setFieldTouched("companyId", true)}
            errors={errors.companyId}
            touched={touched.companyId}
            formstyle="form-input select-z-index"
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="companyProjectId"
            name="companyProjectId"
            label={trans("Company Project")}
            placeholder={
              !selectedCompanyId
                ? trans("Select company first")
                : projectsLoading
                  ? trans("Loading projects...")
                  : trans("Select Company Project")
            }
            options={companyProjectOptions}
            value={values.companyProjectId}
            onChange={(val) => {
              setFieldValue("companyProjectId", val);
              setSelectedProjectId(val.projectId);
              // Clear trades when project changes
              setFieldValue("trades", []);
            }}
            onBlur={() => setFieldTouched("companyProjectId", true)}
            errors={errors.companyProjectId}
            touched={touched.companyProjectId}
            formstyle="form-input select-z-index"
            disabled={!selectedCompanyId || projectsLoading}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="trades"
            name="trades"
            label={trans("Trades")}
            placeholder={
              !values.companyProjectId
                ? trans("Select company project first")
                : tradesLoading
                  ? trans("Loading trades...")
                  : trans("Select Trades")
            }
            options={tradeOptions}
            value={values.trades || []}
            onChange={(val) => setFieldValue("trades", val || [])}
            onBlur={() => setFieldTouched("trades", true)}
            errors={errors.trades}
            touched={touched.trades}
            formstyle="form-input select-z-index"
            mode="multiple"
            maxTagCount="responsive"
            disabled={!values.companyProjectId || tradesLoading}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="assignedZones"
            name="assignedZones"
            label={trans("Assigned Zones")}
            placeholder={
              !selectedCompanyId
                ? trans("Select company first")
                : tradesLoading
                  ? trans("Loading zones...")
                  : trans("Select zones")
            }
            options={zoneOptions}
            value={values.assignedZones || []}
            onChange={(val) => setFieldValue("assignedZones", val || [])}
            onBlur={() => setFieldTouched("assignedZones", true)}
            errors={errors.assignedZones}
            touched={touched.assignedZones}
            formstyle="form-input select-z-index"
            mode="multiple"
            maxTagCount="responsive"
            disabled={!values.companyProjectId || tradesLoading}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="role"
            name="role"
            label={trans("Role")}
            placeholder={trans("Select role")}
            options={roleOptions}
            value={values.role}
            onChange={(val) => setFieldValue("role", val)}
            onBlur={() => setFieldTouched("role", true)}
            errors={errors.role}
            touched={touched.role}
            formstyle="form-input select-z-index"
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="memberUserIds"
            name="memberUserIds"
            label={trans("Member Users")}
            placeholder={trans("Select Member Users")}
            options={userOptions}
            value={values.memberUserIds || []}
            onChange={(val) => setFieldValue("memberUserIds", val || [])}
            onBlur={() => setFieldTouched("memberUserIds", true)}
            errors={errors.memberUserIds}
            touched={touched.memberUserIds}
            formstyle="form-input select-z-index"
            mode="multiple"
            maxTagCount="responsive"
          />
        </div>
        <div className="col-lg-12 mt-1rem">
          {isEditing && attachments && attachments.length > 0 && (
            <div className="mb-3">
              <label className="form-label">
                {trans("Existing Attachments")}
              </label>
              <EditAttachments attachments={attachments} viewOnly={false} />
            </div>
          )}
          <FileUploader
            onFileSelect={handleFileSelect}
            file={newAttachments}
            multiple={true}
            label={trans("Attachments")}
          />
        </div>
      </div>
    </div>
  );
};

export default TeamFormBody;
